import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Modal,
  Multiselect,
  SpaceBetween,
  type MultiselectProps,
} from '@amzn/awsui-components-react';
import type { AccountCandidate } from '@amzn/ivthub-client';
import { useAccountSearch } from '../useAccountSearch';
import type { LighthouseTarget } from '../useLighthouseEdits';

/**
 * `Add Lighthouse Account` — pick accounts, Confirm, close. Figma: the dialog on the lighthouse
 * block (empty state + two-token state). Nothing is written here: Confirm hands the picks to the
 * page, which runs the writes and reports the outcome above the block (see useLighthouseEdits).
 *
 * Mounted per opening by the caller, so every opening starts from an empty selection and a fresh
 * search — Cloudscape will not re-ask for a term it has already been given (see useAccountSearch).
 *
 * ⭐ The selection lives in THIS component's state as whole option objects, not as ids looked up
 * in the current options. Multiselect computes the next selection and draws the tokens from
 * `selectedOptions` alone, so a pick survives any number of later searches that no longer list it.
 */

export interface AddLighthouseAccountModalProps {
  /** The page's industry selector — the candidates are scoped to it. */
  industry: string | null;
  onDismiss: () => void;
  onConfirm: (targets: LighthouseTarget[]) => void;
}

/**
 * A candidate as a Multiselect option: the English name alone on the first line, and the Chinese
 * name and the id together on the second (user, 2026-09-30). The name falls back to the id, which
 * the contract makes required — and the id is then not repeated underneath.
 */
export function toOption(c: AccountCandidate): MultiselectProps.Option {
  const id = c.accountId ?? '';
  const named = c.accountName !== undefined && c.accountName !== '';
  const aka = c.accountAkaName !== undefined && c.accountAkaName !== '' ? c.accountAkaName : undefined;
  // The id stays on every named row: two accounts can share a name, and it is what a pasted
  // SFDC id is matched against.
  const second = [aka, named ? id : undefined].filter((x): x is string => x !== undefined);
  return {
    value: id,
    label: named ? c.accountName : id,
    description: second.length > 0 ? second.join(' · ') : undefined,
    // Shown rather than hidden: a search for a customer who is already a lighthouse should say
    // so, not come back looking like "no such account".
    disabled: c.isLighthouse === true,
    tags: c.isLighthouse === true ? ['已是灯塔'] : undefined,
  };
}

export function AddLighthouseAccountModal({ industry, onDismiss, onConfirm }: AddLighthouseAccountModalProps) {
  const search = useAccountSearch(industry);
  const [selected, setSelected] = useState<readonly MultiselectProps.Option[]>([]);
  const options = useMemo(() => search.candidates.map(toOption), [search.candidates]);

  const confirm = () =>
    onConfirm(
      selected
        .filter((o) => o.value)
        .map((o) => ({ accountId: o.value!, label: o.label ?? o.value! })),
    );

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Add Lighthouse Account"
      // ⚠️ Default width on purpose — tried `large` and reverted (user, 2026-09-30). The
      // Multiselect's list is capped at 465px (`maxWidth: getBreakpointValue('xxs')` in
      // multiselect/internal.js), and with `expandToViewport` — needed so the modal does not clip
      // the list — its minimum is min(trigger, 465px) too (`getDropdownMinWidth`). So a wider modal
      // only widened the input, leaving a much narrower list hanging under it.
      closeAriaLabel="关闭"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" disabled={selected.length === 0} onClick={confirm}>
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField label="Select Account(s)">
        <Multiselect
          selectedOptions={selected}
          onChange={({ detail }) => setSelected(detail.selectedOptions)}
          options={options}
          filteringType="manual"
          onLoadItems={search.onLoadItems}
          statusType={search.status}
          placeholder="Account ID or Account Name"
          filteringPlaceholder="Account ID or Account Name"
          filteringAriaLabel="搜索客户"
          loadingText="搜索中…"
          errorText="搜索失败"
          recoveryText="重试"
          finishedText={options.length > 0 ? '已显示全部结果' : undefined}
          empty="没有可添加的客户：只能搜到属于你有权限的行业的客户"
          deselectAriaLabel={(o) => `移除 ${o.label ?? o.value ?? ''}`}
          expandToViewport
        />
      </FormField>
    </Modal>
  );
}
